import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { injector } from 'Injector';
import { storage } from 'utils/storage';
import { StorageInteractor, STORAGE_INTERACTOR_KEY } from 'utils/storageInteractor';
import { STATUS_UNAUTHORIZED, LOCALES_MAPPING_FOR_BACKEND } from 'utils/constants';
import { Locales } from './enums';

/**
 * The list envelope the backend wraps every paginated collection in. Declared here because it
 * is the one shape shared by every repo: `evaluation/api.ts` had it, `distribution/api.ts` had
 * a private copy wired to nothing, and the rest read `response.data.data` through `any`.
 */
export interface PaginatedDTO<T> {
  data: Array<T>;
  meta: { pagination: { total_pages: number } };
}

const SKOLEROM_SSO_AUTH_COOKIE = 'sso-authcookie-skolerom';

// Falls back to '/' so that locally — where REACT_APP_BASE_URL is empty and requests
// go through the dev proxy — the many call sites written as `api/teacher/...` without
// a leading slash still resolve to /api/..., instead of being joined onto the current
// page path. axios' combineURLs normalises both spellings against a '/' base.
const API = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL || '/',
  headers: {
    'content-type': 'application/json'
  }
});

const logoutUserIfSessionCookieIsNotPresent = (session: StorageInteractor) => {
  if (storage.local.isDevelopment()) return;
  if (window.location.pathname === '/logout') return;
  if (document.cookie.split(SKOLEROM_SSO_AUTH_COOKIE).length > 1) return;

  session.logOut();

  window.location.reload();
};

const onRequestFulfilled = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig> => {
  if (config.headers && !config.headers.Authorization) {
    const storageInteractor = injector.get<StorageInteractor>(STORAGE_INTERACTOR_KEY);
    const locale = storageInteractor.getCurrentLocale() as Locales;
    const token = storageInteractor.getToken();

    if (token) {
      logoutUserIfSessionCookieIsNotPresent(storageInteractor);

      config.headers.Authorization = `Bearer ${token}`;
    }

    if (LOCALES_MAPPING_FOR_BACKEND[locale]) {
      config.headers['Accept-Language'] = LOCALES_MAPPING_FOR_BACKEND[locale];
    }
  }

  return config;
};

// `unknown`, and rethrown as-is: an interceptor must pass the original error through or the
// narrowing every repo now does on it stops working.
const onRequestRejected = async (error: unknown) => Promise.reject(error instanceof Error ? error : new Error(String(error)));

const onResponseFulfilled = (response: AxiosResponse) => response;

const onResponseRejected = async (error: unknown) => {
  if (axios.isAxiosError(error) && error.response?.status === STATUS_UNAUTHORIZED) {
    injector.get<StorageInteractor>(STORAGE_INTERACTOR_KEY).logOut();
    window.location.href = '/login';
  }

  return Promise.reject(error instanceof Error ? error : new Error(String(error)));
};

API.interceptors.request.use(onRequestFulfilled, onRequestRejected);

API.interceptors.response.use(onResponseFulfilled, onResponseRejected);

export { API };
